import type {
  EventType,
  PaymentResponse,
  RefundResponse,
  DisputeResponse,
  MandateResponse,
  PayoutResponse,
  LifecycleWebhook,
  SubscriptionDisputeWebhook,
  SubscriptionWebhookContent,
} from '../types';

/**
 * The payload of a webhook event, tagged by kind.
 *
 * Mirrors the backend's `{ "type": …, "object": … }` envelope: `type` names the
 * payload shape and `object` carries it. Narrow on `content.type` to get a
 * fully-typed `object`:
 *
 * ```typescript
 * if (event.content.type === 'payment_details') {
 *   event.content.object.payment_id; // typed as PaymentResponse
 * }
 * ```
 */
export type WebhookContent =
  | { type: 'payment_details'; object: PaymentResponse }
  | { type: 'refund_details'; object: RefundResponse }
  | { type: 'dispute_details'; object: DisputeResponse }
  | { type: 'mandate_details'; object: MandateResponse }
  | { type: 'payout_details'; object: PayoutResponse }
  | { type: 'subscription_details'; object: SubscriptionWebhookContent };

/**
 * Whether a `subscription_details` payload is a lifecycle snapshot
 * ({@link LifecycleWebhook}) rather than a cycle chargeback or a paid invoice.
 *
 * Three shapes share `content.type`; what marks a lifecycle snapshot on the
 * wire is `transition_id`, which neither of the other two carries. Narrowing
 * on it — rather than on `event_type` — keeps a handler correct for a
 * lifecycle event added after it was written. A payload that fails this check
 * is not yet a paid invoice: test {@link isSubscriptionDisputeWebhook} next.
 *
 * @example
 * ```typescript
 * if (event.content.type === 'subscription_details') {
 *   const object = event.content.object;
 *   if (isSubscriptionLifecycleWebhook(object)) {
 *     object.status; // SubscriptionStatus at the transition
 *     object.cancel_at_period_end; // true for a scheduled cancellation
 *   } else if (isSubscriptionDisputeWebhook(object)) {
 *     object.amount; // what this chargeback took from the cycle
 *   } else {
 *     object.invoice; // the paid invoice
 *   }
 * }
 * ```
 */
export function isSubscriptionLifecycleWebhook(
  object: SubscriptionWebhookContent,
): object is LifecycleWebhook {
  return typeof (object as Partial<LifecycleWebhook>).transition_id === 'string';
}

/**
 * Whether a `subscription_details` payload is a chargeback against a
 * subscription cycle ({@link SubscriptionDisputeWebhook}, sent as
 * `invoice_disputed`).
 *
 * It is the only `subscription_details` shape that carries
 * `connector_dispute_id`; neither a lifecycle snapshot nor a paid invoice does.
 */
export function isSubscriptionDisputeWebhook(
  object: SubscriptionWebhookContent,
): object is SubscriptionDisputeWebhook {
  return typeof (object as Partial<SubscriptionDisputeWebhook>).connector_dispute_id === 'string';
}

/**
 * A parsed and verified Delopay webhook event.
 *
 * Matches the signed wire body exactly:
 * `{ merchant_id, event_id, event_type, content: { type, object }, timestamp }`.
 *
 * - `event_type` identifies the event, e.g. `'payment_succeeded'`.
 * - `content.type` tags the payload kind, e.g. `'payment_details'`.
 * - `content.object` is the payload; narrow on `content.type` to type it.
 */
export interface WebhookEvent {
  /** ID of the merchant that owns this event. */
  merchant_id: string;
  /** Unique ID for this event (stable across delivery retries). */
  event_id: string;
  /** Event type identifier, e.g. `'payment_succeeded'` or `'refund_succeeded'`. */
  event_type: EventType;
  /** The event payload, tagged by kind. Narrow on `content.type` to type `object`. */
  content: WebhookContent;
  /** ISO 8601 timestamp at which the webhook was sent. */
  timestamp: string;
}

function hexToBytes(hex: string): Uint8Array | null {
  if (hex.length === 0 || hex.length % 2 !== 0) return null;
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    const byte = Number.parseInt(hex.slice(i, i + 2), 16);
    if (Number.isNaN(byte)) return null;
    bytes[i / 2] = byte;
  }
  return bytes;
}

export const Webhooks = {
  /**
   * Verify the signature of an incoming Delopay webhook and return the parsed event.
   *
   * Delopay signs each outgoing webhook with HMAC-SHA512 over the raw request body,
   * using your shop's webhook secret (the *payment response hash key* configured on
   * the shop). The hex-encoded digest is delivered in the `X-Webhook-Signature-512`
   * HTTP header.
   *
   * Uses the Web Crypto API (`globalThis.crypto.subtle`), so it runs unchanged in
   * Node 18+, modern browsers, Deno, Bun, and edge runtimes (Cloudflare Workers, Vercel Edge).
   *
   * Available as a static property on the `Delopay` class
   * (`Delopay.webhooks.verify`) and does not require a client instance.
   *
   * @param rawBody - The raw request body. Pass the original bytes (`Uint8Array` /
   *   `Buffer`) when possible; if you pass a string, it must be the unmodified UTF-8
   *   text of the request body. Do **not** parse it before passing.
   * @param signatureHeader - The value of the `X-Webhook-Signature-512` HTTP header.
   * @param secret - Your shop's webhook signing secret.
   * @returns Promise that resolves to the parsed webhook event.
   * @throws {Error} When the signature header is malformed or does not match the body.
   *
   * @example
   * ```typescript
   * // Express example
   * app.post('/webhook', express.raw({ type: 'application/json' }), async (req, res) => {
   *   try {
   *     const event = await Delopay.webhooks.verify(
   *       req.body, // Buffer from express.raw()
   *       req.header('x-webhook-signature-512') ?? '',
   *       process.env.DELOPAY_WEBHOOK_SECRET!,
   *     );
   *     console.log(event.event_type, event.content.object);
   *     res.sendStatus(200);
   *   } catch {
   *     res.status(400).send('Invalid signature');
   *   }
   * });
   * ```
   */
  async verify(
    rawBody: string | Uint8Array,
    signatureHeader: string,
    secret: string,
  ): Promise<WebhookEvent> {
    const subtle = globalThis.crypto?.subtle;
    if (!subtle) {
      throw new Error(
        'Web Crypto unavailable: Delopay.webhooks.verify requires globalThis.crypto.subtle (Node 18+, modern browsers, Workers, Deno)',
      );
    }

    const signatureBytes = hexToBytes(signatureHeader.trim());
    if (!signatureBytes) {
      throw new Error('Invalid webhook signature format');
    }

    const encoder = new TextEncoder();
    const bodyBytes = typeof rawBody === 'string' ? encoder.encode(rawBody) : rawBody;

    // `TextEncoder.encode` returns `Uint8Array<ArrayBufferLike>` in current lib.dom.d.ts,
    // but `crypto.subtle.*` wants `BufferSource` (backed by `ArrayBuffer`). At runtime the
    // underlying buffer is always an `ArrayBuffer` — cast to quiet the type checker.
    const asBufferSource = (bytes: Uint8Array): BufferSource => bytes as unknown as BufferSource;
    const key = await subtle.importKey(
      'raw',
      asBufferSource(encoder.encode(secret)),
      { name: 'HMAC', hash: 'SHA-512' },
      false,
      ['verify'],
    );

    const valid = await subtle.verify(
      'HMAC',
      key,
      asBufferSource(signatureBytes),
      asBufferSource(bodyBytes),
    );

    if (!valid) {
      throw new Error('Invalid webhook signature');
    }

    const bodyText =
      typeof rawBody === 'string' ? rawBody : new TextDecoder('utf-8').decode(rawBody);
    return JSON.parse(bodyText) as WebhookEvent;
  },
};
